import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link2, X, Search, Wrench, Unlink, Check } from 'lucide-react';

const AttachDevisModal = ({ isOpen, onClose, devisId, devisType, devisRef, devisTotal, onAttached }) => {
  const { api } = useAuth();
  const [repairs, setRepairs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [attaching, setAttaching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setSuccess('');
    setSearch('');
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('/devices/list-for-link');
        setRepairs(res.data || []);
      } catch (err) { setError('Erreur chargement des reparations'); }
      finally { setLoading(false); }
    };
    load();
  }, [isOpen, api]);

  const handleAttach = async (ref) => {
    setAttaching(true);
    setError('');
    try {
      await api.post(`/devices/${ref}/attach-devis`, {
        devis_id: devisId,
        devis_type: devisType,
      });
      setSuccess(`Devis attache a ${ref}`);
      setTimeout(() => { onAttached && onAttached(); onClose(); }, 1200);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'attachement');
    }
    finally { setAttaching(false); }
  };

  const filtered = repairs.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return r.ref?.toLowerCase().includes(s) ||
           r.client_name?.toLowerCase().includes(s) ||
           r.model?.toLowerCase().includes(s);
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/[0.08] shadow-2xl overflow-hidden"
        style={{ background: 'linear-gradient(145deg, rgba(18,18,28,0.99), rgba(12,12,20,0.99))' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]" data-testid="attach-devis-modal">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Attacher a une reparation</h3>
              <p className="text-[11px] text-gray-500">Devis {devisRef} - {devisTotal?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-white/[0.06] hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-white/[0.06]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
            <input type="text" placeholder="Rechercher par ref, client ou modele..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/20 outline-none transition-all"
              data-testid="attach-devis-search" />
          </div>
        </div>

        {/* Messages */}
        {error && <div className="mx-5 mt-3 px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs">{error}</div>}
        {success && <div className="mx-5 mt-3 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs flex items-center gap-2"><Check className="w-4 h-4" />{success}</div>}

        {/* List */}
        <div className="px-5 py-3 max-h-[350px] overflow-y-auto space-y-2">
          {loading ? (
            <div className="py-8 text-center"><div className="loading-spinner mx-auto"></div></div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">
              {search ? 'Aucune reparation trouvee' : 'Aucune reparation en cours'}
            </div>
          ) : (
            filtered.map(r => {
              const isLinked = !!r.linked_devis_id;
              return (
                <div key={r.ref} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                  isLinked ? 'border-amber-500/20 bg-amber-500/5 opacity-60' : 'border-white/[0.06] bg-white/[0.03] hover:bg-white/[0.06]'
                }`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-orange-500/20 flex items-center justify-center shrink-0">
                      <Wrench className="w-4 h-4 text-orange-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-cyan-400">{r.ref}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-gray-400">{r.status}</span>
                      </div>
                      <p className="text-xs text-gray-400 truncate">{r.client_name} - {r.model}</p>
                      {isLinked && <p className="text-[10px] text-amber-400">Devis deja attache</p>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleAttach(r.ref)}
                    disabled={attaching || isLinked}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0 ${
                      isLinked
                        ? 'bg-gray-500/10 text-gray-600 cursor-not-allowed'
                        : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                    }`}
                    data-testid={`attach-to-${r.ref}`}
                  >
                    {attaching ? '...' : isLinked ? 'Lie' : 'Attacher'}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/[0.06]">
          <p className="text-[10px] text-gray-600 text-center">Le montant du devis remplacera le prix de reparation. Le diagnostic deviendra une remise.</p>
        </div>
      </div>
    </div>
  );
};

export default AttachDevisModal;
